import type { StreamCache } from "./cache";
import type { Stream } from "./stream";
import type { OakStreamInternalOptions, OakStreamResult } from "./types";

import { createInitialStreamState } from "./state";
import { traceOakStream } from "./trace";

export class StreamObserver {
  private readonly client: StreamCache;
  /**
   * Listeners are callbacks provided by react from
   * instances of the useStream hook. In practice, there
   * will only be one per observer.
   */
  private readonly listeners = new Set<() => void>();
  private readonly options: OakStreamInternalOptions;
  private stream: Stream | null = null;
  private isMounted = false;
  private isEnabled: boolean;
  private currentResult: OakStreamResult;

  constructor(client: StreamCache, options: OakStreamInternalOptions) {
    this.client = client;
    this.options = options;
    this.isEnabled = options.enabled ?? true;
    this.currentResult = this.createResult();
  }

  getStream() {
    if (this.stream === null) {
      throw new Error("StreamObserver is not mounted.");
    }

    return this.stream;
  }

  /**
   * Resolves the shared stream and registers this observer outside the constructor.
   * This split was introduced so `useOakStream` can create `StreamObserver` instances
   * during render without mutating the cache or subscription graph until the effect
   * commit phase, which keeps the hook safe under StrictMode and interrupted renders.
   */
  mount() {
    if (this.isMounted) {
      return;
    }

    traceOakStream("observer", "mount", {
      url: this.options.url,
      initialLastEventId: this.options.initialLastEventId,
      enabled: this.isEnabled,
    });
    const stream = this.client.build({
      url: this.options.url,
      initialLastEventId: this.options.initialLastEventId,
      getIdTokenRefreshIfExpired: this.options.getIdTokenRefreshIfExpired,
    });

    this.stream = stream;
    stream.addObserver(this);
    this.isMounted = true;
    this.updateCurrentResult(false);
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  getCurrentResult = () => this.currentResult;

  setEnabled(enabled: boolean): Promise<void> {
    if (this.isEnabled === enabled) {
      return Promise.resolve();
    }

    traceOakStream("observer", "setEnabled", {
      url: this.options.url,
      enabled,
    });
    this.isEnabled = enabled;

    if (!enabled) {
      this.detach();
      return Promise.resolve();
    }

    this.mount();

    return this.getStream().connect();
  }

  connect(): Promise<void> {
    if (!this.isEnabled) {
      return Promise.resolve();
    }

    traceOakStream("observer", "connect", {
      url: this.options.url,
      initialLastEventId: this.options.initialLastEventId,
    });
    this.mount();

    return this.getStream().connect();
  }

  restart(): Promise<void> {
    if (!this.isEnabled) {
      return Promise.resolve();
    }

    traceOakStream("observer", "restart", {
      url: this.options.url,
      initialLastEventId: this.options.initialLastEventId,
    });
    this.mount();

    return this.getStream().restart();
  }

  onStreamUpdated() {
    const nextResult = this.createResult();
    traceOakStream("observer", "streamUpdated", {
      url: this.options.url,
      status: nextResult.status,
      lastEventId: nextResult.lastEventId,
      dataLength: nextResult.data.length,
      hasError: Boolean(nextResult.error),
    });
    this.currentResult = nextResult;

    for (const listener of this.listeners) {
      listener();
    }
  }

  destroy() {
    traceOakStream("observer", "destroy", {
      url: this.options.url,
      initialLastEventId: this.options.initialLastEventId,
    });
    this.detach();
    this.listeners.clear();
    this.updateCurrentResult(false);
  }

  private detach() {
    if (this.stream !== null) {
      this.stream.removeObserver(this);
      this.stream = null;
    }

    this.isMounted = false;
  }

  private createResult(): OakStreamResult {
    const state =
      this.stream?.getState() ?? createInitialStreamState(this.options.initialLastEventId);

    return {
      data: state.data,
      error: state.error,
      status: state.status,
      lastEventId: state.lastEventId,
      restart: () => this.restart(),
    };
  }

  /**
   * Keeps the external-store snapshot reference stable unless an observable field
   * actually changes. `useSyncExternalStore` treats a new snapshot object as an
   * update, so this prevents lifecycle bookkeeping from causing redundant rerenders
   * or render loops when the stream state is logically unchanged.
   */
  private updateCurrentResult(notifyListeners: boolean) {
    const nextResult = this.createResult();
    const didResultChange =
      this.currentResult.data !== nextResult.data ||
      this.currentResult.error !== nextResult.error ||
      this.currentResult.status !== nextResult.status ||
      this.currentResult.lastEventId !== nextResult.lastEventId;

    if (!didResultChange) {
      return;
    }

    this.currentResult = nextResult;

    if (!notifyListeners) {
      return;
    }

    for (const listener of this.listeners) {
      listener();
    }
  }
}
